import clsx from 'clsx';
import Icon from 'components/atoms/Icon';
import ListWithIcon from 'components/molecules/ListWithIcon/ListWithIcon';
import {
  ProjectPageGetProjectByIdDocument,
  ProjectStatus,
  useInterviewStageDeleteMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import {
  Button,
  IconType,
  Modal,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './DeleteStage.module.scss';

interface IDeleteInterviewStage {
  subId: string;
  projectStatus: ProjectStatus;
  disabled?: boolean;
  projectId: number;
}

const MODAL_CONTENT: Record<ProjectStatus, { content: React.ReactNode }> = {
  [ProjectStatus.Published]: {
    content: (
      <>
        <p className={styles.modalContentRow}>
          All Interview Guide Blueprints and links to the role will be
          permanently deleted. This cannot be undone.{' '}
        </p>
        <p className={styles.modalContentRow}>
          Interview stages can be re-added, but all settings will be lost.
        </p>
        <div className={styles.listContainer}>
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.warningIcon)}>
                <Icon icon="warning" />
              </div>
            }
            title="Deleted with stage"
            listItems={[
              'Stage settings',
              'Interview Guide Blueprints & Interview Guides',
              'Interview links to Role Skills Profile or candidates',
            ]}
          />
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.checkIcon)}>
                <Icon icon="check" />
              </div>
            }
            title="Preserved on role"
            listItems={['Candidates']}
          />
        </div>
      </>
    ),
  },
  [ProjectStatus.Draft]: {
    content: (
      <p>
        This action will permanently remove the stage and its associated
        settings from the role and cannot be undone.
      </p>
    ),
  },
  [ProjectStatus.Closed]: {
    content: null,
  },
  [ProjectStatus.Review]: {
    content: null,
  },
};

const DeleteInterviewStage: FC<IDeleteInterviewStage> = (props) => {
  const { handleMsgType } = useNotification();
  const [modalOpen, setModalOpen] = useState(false);

  const [removeInterviewStage] = useInterviewStageDeleteMutation({
    variables: {
      subId: props.subId,
    },
    refetchQueries: [
      {
        query: ProjectPageGetProjectByIdDocument,
        variables: { id: props.projectId },
      },
    ],
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: 'Successfully removed interview stage',
        dismissable: true,
      });
      setModalOpen(false);
    },
    onError: (error) => {
      handleMsgType({
        type: TNotification.error,
        title: 'Failed to remove interview stage',
        message: error?.message,
        dismissable: true,
      });
    },
  });

  const handleDelete = () => {
    removeInterviewStage();
  };

  return (
    <>
      <Button
        onClick={() => setModalOpen(true)}
        size="medium"
        variant="secondary"
        data-testid="stage-header__delete-stage-button"
        disabled={props.disabled}
      >
        Remove
      </Button>
      <Modal
        header="Remove interview stage?"
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        headerIconType={IconType.WARNING}
        buttonsProps={{
          confirmButtonChildren: 'Remove stage',
          onConfirmButtonClick: handleDelete,
        }}
      >
        <div className={styles.modalContent}>
          {MODAL_CONTENT[props.projectStatus].content}
        </div>
      </Modal>
    </>
  );
};

export default DeleteInterviewStage;
